import { ActionContext, Module } from 'vuex'
import { OnboardingState, RootState } from '../store/state_models'

import { AuthUser } from '../models'
import { IUpdateStripeCustomer } from '../types/customer'
import InternalToolsService from '../services/InternalToolsService'
import { OnboardingService } from '../services'
import { USER_ROLE } from '../models/user'
import { aiEmployeePromoRoutes } from '../routes/constants/agency'
import { conversationAIRouteNames } from '../routes/constants/location'
import moment from 'moment'

const summerOfAIPromoFunnels = [
  // staging
  'qPJuzvENA7bwH7gAekc1',
  'lPJuzvENA7bwH7gAekc2',
  // production
  'ISbJmjFkPujrmZy2su17',
]

const RewardAmountMappings = {
  JOINED_KICKOFF_CALL_V2: 25,
  JOINED_KICKOFF_CALL: 15,
  FIRST_LAUNCHPAD_ACTION_V2: 15,
  FIRST_LAUNCHPAD_ACTION: 25,
  CONVERTED_TO_PAID_USER: 50
}


const isActiveStatus = (companyStatus: string) => {
  const allowedStatus = ['active', 'active_trial', 'active_trial-extension']
  return allowedStatus.includes(companyStatus)
}

const isAdmin = (role: string) => {
  return role === USER_ROLE.ADMIN
}

export const onboarding: Module<OnboardingState, RootState> = {
  namespaced: true,
  state: {
    onboardingInfo: {},
    emailVerified: false,
    phoneNumberVerified: false,
    widgetState: false,
    version: null,
    widgetPosition: {
      x: null,
      y: null,
    },
    startValuePropTourGuide: false,
    showSummerOfAIMenu: undefined,
    valuePropsProductIds: [],
    valuePropLaunchpadType: 'location',
    rewardAmounts: undefined,
  },
  mutations: {
    set: (state: { version: number }, payload: { version?: number }) => {
      if (payload.version) {
        state.version = payload.version
      }
    },
    setWidgetPosition: (state: { widgetPosition: object }, payload: { x?: number; y?: number }) => {
      state.widgetPosition.x = payload.x
      state.widgetPosition.y = payload.y
    },
    setOnboardingInfo: (state: { onboardingInfo: object }, payload: object) => {
      state.onboardingInfo = payload
    },
    setStartValuePropTourGuide: (
      state: { startValuePropTourGuide: boolean },
      payload: { startValuePropTourGuide: boolean }
    ) => {
      state.startValuePropTourGuide = payload.startValuePropTourGuide
    },
    setShowSummerOfAIMenu(state, payload: boolean) {
      state.showSummerOfAIMenu = payload
    },
    setValuePropsProductIds: (
      state: { valuePropsProductIds: string[]; valuePropLaunchpadType: string },
      payload: { productIds: string[]; type: string }
    ) => {
      state.valuePropsProductIds = payload.productIds || []
      state.valuePropLaunchpadType = payload.type || 'location'
    },
    setRewardAmounts: (
      state: OnboardingState,
      payload: {
        kickoffRewardAmount: number
        launchpadRewardAmount: number
        successRewardAmount: number
      }
    ) => {
      state.rewardAmounts = payload
    },
  },
  actions: {
    verifyStatus: {
      handler: (context: ActionContext<OnboardingState, RootState>): Promise<OnboardingState> => {
        return new Promise(async resolve => {
          try {
            const credentials = new AuthUser(await context.dispatch('auth/get', null, { root: true }))
            const data = await OnboardingService.verifyStatus(credentials.companyId)
            resolve({
              emailVerified: data.emailVerified || false,
              phoneNumberVerified: data.phoneNumberVerified || false,
            })
          } catch {
            resolve({ emailVerified: false, phoneNumberVerified: false })
          }
        })
      },
    },
    updateStripeCustomer: {
      handler: (context: ActionContext<OnboardingState, RootState>, body: IUpdateStripeCustomer): Promise<any> => {
        return new Promise(async (resolve, reject) => {
          try {
            const data = await OnboardingService.updateStripeCustomer(body)
            resolve(data)
          } catch (e) {
            reject(e)
          }
        })
      },
    },
    getTrialDaysLeft: {
      handler: (context: ActionContext<OnboardingState, RootState>, companyId: string): Promise<number> => {
        return new Promise(async resolve => {
          try {
            const { status, subscription } = await InternalToolsService.getCompanyBillingPlan(companyId)
            if (status.startsWith('trialing')) {
              const trialEnd = moment.unix(subscription.trialEnd)
              const daysLeft = trialEnd.diff(moment(), 'days')
              resolve(daysLeft)
            }

            resolve(0)
          } catch (e) {
            console.warn('Error getting trial days left', e)
            resolve(0)
          }
        })
      },
    },
    widgetState: {
      handler: (context: ActionContext<OnboardingState, RootState>, state: boolean): void => {
        // context.state.widgetState = state
        context.commit('set', { widgetState: state })
      },
    },
    getWidgetState: {
      handler: (context: ActionContext<OnboardingState, RootState>): boolean => {
        return context.state.widgetState
      },
    },
    getWidgetPosition: {
      handler: (context: ActionContext<OnboardingState, RootState>): boolean => {
        return context.state.widgetPosition
      },
    },
    setWidgetPosition: {
      handler: (context: ActionContext<OnboardingState, RootState>, widgetPosition: object): void => {
        context.commit('setWidgetPosition', { x: widgetPosition?.x, y: widgetPosition?.y })
      },
    },
    getOnboardingVersion: {
      handler: (context: ActionContext<OnboardingState, RootState>, { companyId }): Promise<any> => {
        return new Promise(async resolve => {
          try {
            if (context.state.version) {
              return resolve(context.state.version)
            }
            const data = await OnboardingService.getVersion({ companyId })
            if (data) {
              context.commit('set', { version: data })
              resolve(data)
            } else {
              resolve(null)
            }
          } catch {
            resolve(null)
          }
        })
      },
    },
    getValuePropProductIds: {
      handler: (
        context: ActionContext<OnboardingState, RootState>,
        { companyId, valuePropId, forceRefresh = false }
      ): Promise<any> => {
        return new Promise(async resolve => {
          if (!valuePropId) {
            return resolve([])
          }

          if (!forceRefresh && (context?.state?.valuePropsProductIds || []).length > 0) {
            return resolve(context?.state?.valuePropsProductIds)
          }
          try {
            const { valueProp } = await OnboardingService.getValuePropProductIds(valuePropId, {
              companyId,
            })
            context.commit('setValuePropsProductIds', valueProp)
            resolve(valueProp?.productIds || [])
          } catch {
            resolve([])
          }
        })
      },
    },
    getOnboardingInfo: {
      handler: (
        context: ActionContext<OnboardingState, RootState>,
        { companyId, forceRefresh = false }
      ): Promise<any> => {
        return new Promise(async resolve => {
          try {
            // Return cached data only if forceRefresh is false and we have data
            if (!forceRefresh && Object.keys(context.state.onboardingInfo).length > 0) {
              return resolve(context.state.onboardingInfo)
            }
            const data = await OnboardingService.getInfo({ companyId })
            if (data) {
              const valuePropId = data.valueProp?.productId
              if (valuePropId) {
                const { valueProp } = await OnboardingService.getValuePropProductIds(data.valueProp?.productId, {
                  companyId,
                })
                context.commit('setValuePropsProductIds', valueProp)
              }
              context.commit('setOnboardingInfo', data)
              resolve(data)
            } else {
              resolve(null)
            }
          } catch {
            resolve(null)
          }
        })
      },
    },
    checkAndSetSummerOfAI: {
      handler: async (
        context: ActionContext<OnboardingState, RootState>,
        {
          locationId,
          companyId,
          companyStatus,
          userRole,
        }: { locationId?: string; companyId?: string; companyStatus?: string; userRole?: string }
      ): Promise<boolean> => {
        try {
          let showSummerOfAIMenu = undefined

          if (locationId) {
            if (context.state.showSummerOfAIMenu === undefined) {
              showSummerOfAIMenu = await context.dispatch(
                'aiLocationPlan/fetchShowSummerOfAIMenu',
                { locationId },
                { root: true }
              )
            }
          }

          if (!locationId) {
            const onboardingInfo = await context.dispatch('getOnboardingInfo', { companyId })

            // if user is admin and company is paid, trial, extended trial, show summer of ai menu
            // for existing agencies as well.
            if (isActiveStatus(companyStatus) && isAdmin(userRole)) {
              showSummerOfAIMenu = true
            }

            // If funnelId is found, check if it is a summer of ai funnel
            let funnelId = onboardingInfo?.funnelId || null
            if (funnelId) {
              if (summerOfAIPromoFunnels.includes(funnelId)) {
                showSummerOfAIMenu = true
              } else {
                showSummerOfAIMenu = false
              }
            }
          }

          // context.commit('setShowSummerOfAIMenu', showSummerOfAIMenu)
          return showSummerOfAIMenu
        } catch (error) {
          console.error('Error in checkAndSetSummerOfAI:', error)
          return false
        }
      },
    },
    getRewardAmount: {
      handler: (
        context: ActionContext<OnboardingState, RootState>,
        { companyId }
      ): Promise<any> => {
        return new Promise(async resolve => {
          try {
            /**
             * - Before 11 Sept 2025:
             * kickof: 25, launchpad: 15, success: 50
             *
             * - After 11 Sept 2025 and before 13 Jan 2026 (additionally, if someone has not completed, they must see new reward amount for that item):
             * kickoff: 15, launchpad: 25, success: 50
             *
             * - After 13 Jan 2026:
             * kickoff: 25, launchpad: 15, success: 50
             *
             * @returns Reward amount object { kickoffRewardAmount: number, launchpadRewardAmount: number, successRewardAmount: number }
             */
            // Return cached data if we have it in state
            if (context.state.rewardAmounts) {
              return resolve(context.state.rewardAmounts)
            }

            const rewardParams: any = { companyId }
            const onboardingInfo = await context.dispatch('getOnboardingInfo', { companyId })
            if (onboardingInfo?.dateAdded || onboardingInfo?.createdAt) {
              rewardParams.dateAdded = onboardingInfo?.dateAdded || onboardingInfo?.createdAt
            } 
            const rewardsEnum = await OnboardingService.getRewardEnum(rewardParams)

            const rewardAmounts = {
              kickoffRewardAmount: RewardAmountMappings[rewardsEnum?.kickoff as keyof typeof RewardAmountMappings],
              launchpadRewardAmount: RewardAmountMappings[rewardsEnum?.launchpad as keyof typeof RewardAmountMappings],
              successRewardAmount: RewardAmountMappings[rewardsEnum?.success as keyof typeof RewardAmountMappings],
            }

            context.commit('setRewardAmounts', rewardAmounts)
            resolve(rewardAmounts)
          } catch (error) {
            console.warn('Error in getting onboarding Info:', error)
            const defaultRewardAmounts = {
              kickoffRewardAmount: RewardAmountMappings.JOINED_KICKOFF_CALL_V2,  // default to 25
              launchpadRewardAmount: RewardAmountMappings.FIRST_LAUNCHPAD_ACTION_V2,  // default to 15
              successRewardAmount: RewardAmountMappings.CONVERTED_TO_PAID_USER,  // default to 50
            }
            context.commit('setRewardAmounts', defaultRewardAmounts)
            resolve(defaultRewardAmounts)
          }
        })
      },
    },
  },
}